import { useState, useCallback, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/brevo`;

export const useBrevoDeals = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, message: '' });
  const [diagnosticLogs, setDiagnosticLogs] = useState([]);
  const [data, setData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const pollIntervalRef = useRef(null);

  const addLog = (type, message, details = null) => {
    setDiagnosticLogs(prev => [...prev, {
      timestamp: new Date().toISOString(),
      type,
      message,
      details
    }]);
  };

  const apiFetch = async (endpoint, options = {}) => {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error ${response.status}: ${errorText}`);
    }
    
    return response.json();
  };

  // Load cached data (instant)
  const loadCachedData = useCallback(async () => {
    try {
      const cachedResponse = await apiFetch('/cached-data');
      
      if (cachedResponse.lastUpdated) {
        setData({
          summary: cachedResponse.summary,
          closedByMonth: cachedResponse.closedByMonth,
          processedDeals: cachedResponse.processedDeals
        });
        setLastUpdated(cachedResponse.lastUpdated);
        addLog('success', `Loaded cached data from ${new Date(cachedResponse.lastUpdated).toLocaleString()}`);
        return true;
      }
      return false;
    } catch (e) {
      addLog('error', 'Failed to load cached data', e.message);
      return false;
    }
  }, []);

  // Poll for refresh status
  const pollRefreshStatus = useCallback(async () => {
    try {
      const status = await apiFetch('/refresh-status');
      
      if (status.in_progress) {
        setProgress(status.progress);
        setLoading(true);
      } else {
        // Refresh complete - load the new cached data
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        
        if (status.last_error && !status.last_error.includes('clone')) {
          setError(status.last_error);
          addLog('error', 'Refresh failed', status.last_error);
        } else {
          await loadCachedData();
          setProgress({ current: 0, total: 0, message: 'Complete!' });
        }
        setLoading(false);
      }
    } catch {
      // Polling errors are expected when refresh isn't active
    }
  }, [loadCachedData]);

  // Trigger a refresh
  const refreshData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0, message: 'Starting refresh...' });
    setDiagnosticLogs([]);
    addLog('info', 'Starting data refresh...');

    try {
      const response = await apiFetch('/refresh', { method: 'POST' });
      
      if (response.status === 'already_running') {
        addLog('info', 'Refresh already in progress');
      } else {
        addLog('success', 'Refresh started');
      }
      
      // Start polling for status
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      pollIntervalRef.current = setInterval(pollRefreshStatus, 1000);
      
    } catch (e) {
      setError(e.message);
      addLog('error', 'Failed to start refresh', e.message);
      setLoading(false);
    }
  }, [pollRefreshStatus]);

  // Initial load - try cached data first
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0, message: 'Loading...' });
    
    // First, check if there's cached data
    const hasCached = await loadCachedData();
    
    if (hasCached) {
      setLoading(false);
      return;
    }
    
    // No cached data - start a refresh
    await refreshData();
  }, [loadCachedData, refreshData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Diagnostic functions
  const runDiagnostic = useCallback(async () => {
    setDiagnosticLogs([]);
    setLoading(true);
    setError(null);
    
    try {
      addLog('info', 'Testing account endpoint...');
      try {
        const account = await apiFetch('/account');
        addLog('success', 'Account endpoint OK', account);
      } catch (e) {
        addLog('error', 'Account endpoint failed', e.message);
      }

      addLog('info', 'Testing pipeline endpoint...');
      try {
        const pipelines = await apiFetch('/pipelines');
        addLog('success', 'Pipeline endpoint OK', pipelines);
      } catch (e) {
        addLog('error', 'Pipeline endpoint failed', e.message);
      }

      addLog('info', 'Testing deals endpoint...');
      try {
        const deals = await apiFetch('/deals?limit=5&offset=0');
        addLog('success', 'Deals endpoint OK', deals);
      } catch (e) {
        addLog('error', 'Deals endpoint failed', e.message);
      }

      addLog('info', 'Testing cached data endpoint...');
      try {
        const cached = await apiFetch('/cached-data');
        if (cached.cached) {
          addLog('success', `Cached data available (${cached.summary?.totalWonDeals || 0} deals, updated ${cached.lastUpdated})`);
        } else {
          addLog('warning', 'No cached data available');
        }
      } catch (e) {
        addLog('error', 'Cached data endpoint failed', e.message);
      }

    } catch (e) {
      addLog('error', 'Diagnostic failed', e.message);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    progress,
    diagnosticLogs,
    data,
    lastUpdated,
    fetchData,
    refreshData,
    runDiagnostic,
    clearLogs: () => setDiagnosticLogs([])
  };
};
